import React, { useState, useRef } from 'react';
import { Card, Form, Modal, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { InfoCircle, DocumentDownload } from 'iconsax-react';
import MyApiClient from '../../../../../api-client/MyApiClient';
import { segnalaMancanti } from '../../../../components/Util/campiMancanti';

const ImportaPlayer = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [fileName, setFileName] = useState('');
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [modalType, setModalType] = useState('success'); // 'success' | 'error'

    const openResult = (message, type) => {
        setModalMessage(message);
        setModalType(type);
        setShowModal(true);
    };

    const downloadCsvTemplate = () => {
        const csvContent = "Nome,Cognome,Email,Telefono,DataNascita\nMario,Rossi,mario.rossi@email.com,3331234567,15/03/1990\nLucia,Bianchi,,3339876543,22/07/1985";
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'template_player.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleCsvUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const csv = e.target.result;
                const lines = csv.split('\n').filter(line => line.trim());

                if (lines.length < 2) {
                    setPlayers([]);
                    openResult('Il file CSV deve contenere almeno un header e una riga di dati.', 'error');
                    return;
                }

                const dataLines = lines.slice(1);
                const newPlayers = [];
                dataLines.forEach((line) => {
                    const columns = line.split(',').map(col => col.trim().replace(/^"|"$/g, ''));
                    if (columns.length >= 4) {
                        newPlayers.push({
                            nome: columns[0] || '',
                            cognome: columns[1] || '',
                            email: columns[2] || '',
                            telefono: columns[3] || '',
                            birthdate: columns[4] || ''
                        });
                    }
                });

                setPlayers(newPlayers);
                if (newPlayers.length === 0) {
                    openResult('Nessun dato valido trovato nel file CSV.', 'error');
                }
            } catch (error) {
                setPlayers([]);
                openResult('Errore durante la lettura del file CSV. Verifica il formato.', 'error');
            }
        };
        reader.readAsText(file);
        event.target.value = '';
    };

    // Converte e valida le righe importate (data in formato dd/MM/yyyy dal template)
    const buildPayload = () => {
        const errorRows = [];
        const payload = [];

        players.forEach((player, index) => {
            const rowNum = index + 1;
            if (!player.nome.trim() || !player.cognome.trim() || !player.telefono.trim()) {
                errorRows.push(`Riga ${rowNum}: nome, cognome e telefono sono obbligatori`);
                return;
            }
            if (player.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(player.email)) {
                errorRows.push(`Riga ${rowNum}: formato email non valido`);
                return;
            }
            const match = player.birthdate.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
            if (!match) {
                errorRows.push(`Riga ${rowNum}: data di nascita non valida (formato dd/MM/yyyy)`);
                return;
            }
            const [, dd, mm, yyyy] = match;
            const birthDate = new Date(`${yyyy}-${mm}-${dd}`);
            birthDate.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (isNaN(birthDate.getTime()) || birthDate > today) {
                errorRows.push(`Riga ${rowNum}: data di nascita non valida`);
                return;
            }
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
            if (age < 18) {
                errorRows.push(`Riga ${rowNum}: il player deve essere maggiorenne (almeno 18 anni)`);
                return;
            }

            payload.push({
                nome: player.nome.trim(),
                cognome: player.cognome.trim(),
                email: player.email.trim() || null,
                telefono: player.telefono.trim(),
                birthdate: `${yyyy}-${mm}-${dd}`
            });
        });

        return { payload, errorRows };
    };

    const handleImport = async () => {
        if (!fileName || players.length === 0) {
            segnalaMancanti([{ campo: 'importa-file', messaggio: fileName ? 'Il file non contiene righe valide: scegline uno conforme al template' : 'Manca il file CSV da importare' }]);
            return;
        }

        const { payload, errorRows } = buildPayload();
        if (errorRows.length > 0) {
            openResult(`Il file contiene errori:\n\n${errorRows.join('\n')}`, 'error');
            return;
        }

        setLoading(true);
        try {
            const apiClient = new MyApiClient();
            const tokenDetailsString = sessionStorage.getItem('userDetails');
            if (tokenDetailsString) {
                const tokenDetails = JSON.parse(tokenDetailsString);
                const token = tokenDetails.idToken;

                const response = await apiClient.bulkCreatePlayers(token, payload);
                if (response.error) {
                    const err = new Error('Errore dal server');
                    err.data = response;
                    throw err;
                }

                const { data } = response;
                const message = data.created > 0
                    ? `${data.created} player importati con successo.`
                    : 'Operazione completata con successo.';
                openResult(message, 'success');
                setPlayers([]);
                setFileName('');
            } else {
                openResult('Errore durante l\'importazione dei player. Riprova.', 'error');
            }
        } catch (error) {
            const errorMessage = (error.data && error.data.error && error.data.error.detail) || error.message || 'Errore durante l\'importazione dei player. Riprova.';
            openResult(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        if (modalType === 'success') navigate('/struttura/clienti/list');
    };

    return (
        <>
            {/* Banner informativo */}
            <div className="sid-info-banner">
                <span className="sid-info-banner__icon"><InfoCircle size={22} variant="Bulk" /></span>
                <div className="sid-info-banner__body">
                    <div className="sid-info-banner__title">Importazione CSV</div>
                    <p>Scarica il template, compila i dati e importa il file (formato: <code>Nome,Cognome,Email,Telefono,DataNascita</code>).</p>
                    <p>Data di nascita: formato <strong>dd/MM/yyyy</strong> — il Player deve essere maggiorenne (almeno 18 anni).</p>
                </div>
            </div>

            <Card>
                <Card.Header>
                    <Card.Title>Importa file CSV</Card.Title>
                    <button type="button" className="btn sid-btn-ghost btn-sm" onClick={downloadCsvTemplate}>
                        <DocumentDownload size={16} className="me-1" /> Scarica template
                    </button>
                </Card.Header>
                <Card.Body>
                    <div>
                        <input type="file" ref={fileInputRef} onChange={handleCsvUpload} accept=".csv" style={{ display: 'none' }} />
                        <div>
                            <Form.Label htmlFor="importa-file">File CSV *</Form.Label>
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                <Form.Control id="importa-file" readOnly value={fileName} placeholder="Nessun file selezionato"
                                    onClick={() => fileInputRef.current?.click()} style={{ maxWidth: 360, cursor: 'pointer' }} />
                                <button type="button" className="btn sid-btn-ghost" onClick={() => fileInputRef.current?.click()}>
                                    Scegli file
                                </button>
                            </div>
                        </div>
                    </div>
                    <p className="sid-help-text mt-3 mb-0">Sono accettati solo file in formato <strong>.csv</strong> conformi al template.</p>
                </Card.Body>
            </Card>

            <div className="d-flex justify-content-end gap-4 mt-4">
                <button type="button" className="btn sid-btn-ghost" onClick={() => navigate('/struttura/clienti/list')}>
                    Annulla
                </button>
                <button type="button" className="btn btn-primary" onClick={handleImport} disabled={loading}>
                    {loading ? 'Importazione…' : 'Importa'}
                </button>
            </div>

            {/* Modal messaggi */}
            <Modal show={showModal} onHide={closeModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{modalType === 'success' ? 'Successo' : 'Errore'}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ whiteSpace: 'pre-line' }}>{modalMessage}</Modal.Body>
                <Modal.Footer className="justify-content-center">
                    <button type="button" className="btn btn-primary" onClick={closeModal}>Chiudi</button>
                </Modal.Footer>
            </Modal>

            {/* Modal caricamento */}
            <Modal show={loading} centered backdrop="static">
                <Modal.Body className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                    <div className="mt-2">Importazione in corso…</div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default ImportaPlayer;
